import { getResponseParsedJson } from "../json/getResponseData";

/**
 * Creates an API fetcher function with the specified locale.
 * @param {object} params - The parameters object.
 * @param {string} [params.locale] - The locale to set in the 'Accept-Language' header.
 * @param {string} [params.channelId] - The channel ID to set in the 'Channel' header.
 * @param {import("../../hooks/useApiFetcher").TwoFactorAuthChallengeStartChallenge} params.startChallenge - A function that takes an object with a challengeId property and returns a promise resolving to a string.
 * @param {() => void} params.onLogout - A function to call when the user needs to be logged out.
 * @returns {import("../../hooks/useApiFetcher").ApiFetcher} The API fetcher function.
 */
export function getApiFetcher({ locale, channelId, startChallenge, onLogout }) {
  /** @type {import("../../hooks/useApiFetcher").ApiFetcher} */
  const apiFetcher = (info, init = {}) => {
    const url = typeof info === "string" ? info : info[0];

    return new Promise((resolve, reject) => {
      (async () => {
        // Create a new Headers object from the existing headers
        const headers = new Headers(init.headers);
        // Set the 'Accept-Language' header to the locale
        if (locale) {
          headers.set("Accept-Language", locale);
        }
        if (channelId) {
          headers.set("Channel", String(channelId));
        }

        headers.set("Accept", "application/json");

        // Update the init object with the new headers
        init.headers = headers;

        try {
          // Proceed with the fetch request
          const initialResponse = await fetch(url, init);

          const parsedJson = await getResponseParsedJson(initialResponse);

          // if the response is valid
          if (
            initialResponse.status >= 200 &&
            initialResponse.status < 400 &&
            initialResponse.status !== 202
          ) {
            resolve({ response: initialResponse, parsedJson });
            return;
          }

          if (initialResponse.status === 401) {
            reject({ response: initialResponse, parsedJson });
            onLogout();
            return;
          }

          // if the response is 202 and the 2FA challenge is set up
          if (
            initialResponse.status === 202 &&
            typeof startChallenge === "function"
          ) {
            try {
              const {
                challenge_id: challengeId,
                method,
                expires_at: expiresAt,
                email,
                phone_number: phoneNumber,
              } = parsedJson?.data?.attributes ?? {};

              if (!challengeId) {
                throw new Error("Challenge ID not found in response");
              }

              let isChallengeCompleted = await startChallenge({
                challengeId,
                method,
                expiresAt,
                email,
                phoneNumber,
              }).catch(() => {
                return false;
              });

              // Check if the challenge token is undefined
              if (isChallengeCompleted === false) {
                reject("Challenge aborted");
                return;
              }

              const urlObj = new URL(url);
              urlObj.searchParams.set(
                "meta[two_factor_authentication][challenge_id]",
                challengeId,
              );

              // Retry the fetch request with the updated URL
              const response = await fetch(urlObj, init);
              const data = await getResponseParsedJson(response);
              resolve({ response, parsedJson: data });
              return;
            } catch (error) {
              reject(error);
              return;
            }
          }

          // Reject the promise with the error
          reject({ response: initialResponse, parsedJson });
        } catch (error) {
          // Reject the promise with the error
          reject(error);
        }
      })();
    });
  };
  return apiFetcher;
}
